import React, { useState, useMemo, useEffect } from "react";
import { sortBy } from "lodash";
import { useSelector, useDispatch } from "react-redux";
import { FormControl } from "@mui/material/";
import { strings } from "../../../../common/i18n";
import { enums, helpers } from "../../../../common/lib";
import { SelectAll } from "../../../../common/components";
import styles from "./styles";
import actions from "../../../../Store/actions";

type Props = {
    defaultValue: Array<any>,
    index: number,
    branchLocation: number,
    onChange: Function
};

/**
 * User-locations Select Component
 */
export default ({
    defaultValue,
    index,
    branch,
    branchLocation,
    onChange
}: Props) => {
    const { accounts, locations, auditsCreate, userLocations, typesList } =
        useSelector((state) => state);

    const dispatch = useDispatch();

    /** current branch auditors */
    const currentAuditors = useMemo(() => {
        return auditsCreate.auditors.find((item) => item.id === index);
    }, [auditsCreate.auditors, index]);

    const [value, setValue] = useState(
        currentAuditors && currentAuditors.selectedUsers
            ? currentAuditors.selectedUsers
            : []
    );

    // aggregate list of user names and their emails
    const items = useMemo(() => {
        let next = userLocations.data.reduce((accum, item) => {
            if (
                item.group === enums.auth.group.auditor &&
                branchLocation === item.location
            ) {
                const auditor = helpers.getListItemForId(accounts, item.user);
                const location = helpers.getListItemForId(
                    locations,
                    item.location
                );

                if (auditor && location) {
                    const { email, firstName, surname } = auditor;
                    const { name } = location;
                    accum.push({
                        location: name,
                        user: `${email} \u2014 ${firstName} ${surname}`,
                        userLocation: item
                    });
                }
            }
            return accum;
        }, []);

        next = sortBy(next, ["location", "user"]);
        next = next.map((item, i) => ({ ...item, index: i }));
        return next;
    }, [accounts, branchLocation, locations, userLocations]);

    /** On error of select */
    const error = useMemo(() => {
        if (!currentAuditors) return null;

        const obj = auditsCreate.data.find(
            (item) => item.location === branchLocation && item.branch === branch
        );

        if (obj.userLocations.length === 0)
            return strings("errors.userLocationsRequired");

        /** one auditor is required to proceed  */
        if (currentAuditors.selectedUsers.length === 0)
            return strings("errors.auditorRequired");

        return null;
    }, [value, currentAuditors, auditsCreate.auditors]);

    /** org settings for auditors */
    const auditorSelect = useMemo(() => {
        /**id for auto select all auditor */
        const autoSelectAuditorValId = 1;
        const { organisationSettings } = typesList;
        const { data } = organisationSettings;
        if (data.length > 0)
            return data
                .find((item) => item.settingId === autoSelectAuditorValId)
                .value.toLowerCase();
        else return "true"; // Adding default true when no options present
    }, [typesList]);

    /** On loading dropdown  */
    useEffect(() => {
        if (defaultValue && items.length) {
            const next = items.filter((item) =>
                defaultValue.find(
                    ({ location, user }) =>
                        item.userLocation.user === user &&
                        item.userLocation.location === location
                )
            );

            /** Preselect auditor if value is true */
            if (auditorSelect === "true") {
                const selectedIds = next.map((item) => item.index);
                const newAuditors = [...auditsCreate.auditors];
                const res = newAuditors.find((item) => item.id === index);
                if (!res) return;
                res["selectedUsers"] = selectedIds;
                dispatch(
                    actions.auditsCreate.create.update({
                        auditors: newAuditors
                    })
                );
            }
        }
    }, [defaultValue, auditorSelect, items]);

    /** On change of select */
    const onValueChange = (next: Array<number>) => {
        setValue(next);
        const nextItems = items
            .filter(({ index }) => next.includes(index))
            .map(({ userLocation }) => userLocation);
        onChange && onChange(nextItems, index, next);
    };

    return (
        <FormControl sx={styles.selectContainer}>
            <SelectAll
                error={error}
                groupByField="location"
                items={items}
                labelField="user"
                onChange={onValueChange}
                value={currentAuditors?.selectedUsers ?? []}
                valueField="index"
            />
        </FormControl>
    );
};
